import {
  EULER_EARN_VAULT_LENS_ADDRESS,
  E_LEND_CHAIN_ID,
  VAULT_LENS_ADDRESS,
  eulerEarnVaultLensAbi,
  vaultLensAbi,
} from '@/config/euler-contracts'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { type Address } from 'viem'
import { useReadContract, useReadContracts } from 'wagmi'
import { extractVaultLensPrice } from '../shared/onchainHelpers'
import { getPriceFromMap, usePricesFromIndexer } from './usePricesFromIndexer'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * From an EulerEarnVaultLens response, find the first eVault strategy address.
 * That lending vault's oracle knows the price of the shared underlying asset.
 */
function extractEVaultStrategyAddress(earnData: unknown): Address | undefined {
  if (!earnData) return undefined
  const info = earnData as any
  const strategies = info.strategies as any[] | undefined
  if (!strategies) return undefined
  const evaultStrategy = strategies.find((s: any) => s.info?.isEVault)
  return evaultStrategy?.strategy as Address | undefined
}

// ---------------------------------------------------------------------------
// Shared on-chain price cache (react-query)
//
// Both useBatchAssetPrices and useAssetPrice write resolved on-chain prices
// to this cache.  Any hook can read from it before making new RPC calls.
// ---------------------------------------------------------------------------

const ONCHAIN_PRICE_CACHE_KEY = ['euler-onchain-asset-prices'] as const

type PriceCache = Map<string, number>

function usePriceCache(key: readonly string[]): PriceCache | undefined {
  const { data } = useQuery<PriceCache>({
    queryKey: key,
    queryFn: () => new Map(),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 5 * 60_000,
  })
  return data
}

function getCachedPrice(cache: PriceCache | undefined, address?: Address): number {
  if (!cache || !address) return 0
  return cache.get(address.toLowerCase()) ?? 0
}

// ---------------------------------------------------------------------------
// VaultLens price cache (react-query)
//
// Keyed by vault address.  Populated by both useBatchAssetPrices (multicall)
// and useAssetPrice (individual calls).  Lets useAssetPrice skip RPC when the
// batch hook already resolved the price, reducing RPC rate-limit pressure.
// ---------------------------------------------------------------------------

const VAULTLENS_PRICE_CACHE_KEY = ['euler-vaultlens-prices'] as const

// ---------------------------------------------------------------------------
// Single asset price
// ---------------------------------------------------------------------------

interface UseAssetPriceParams {
  assetAddress?: Address
  vaultAddress?: Address
  /** Lending vault addresses (e.g. Earn vault strategies) to try via VaultLens.
   *  The first one that returns a non-zero price wins. */
  lendingVaultAddresses?: Address[]
  chainId?: number
  enabled?: boolean
}

interface UseAssetPriceResult {
  priceUSD: number
  isLoading: boolean
  source: 'indexer' | 'vaultLens' | 'lendingVault' | 'earnStrategy' | 'none'
}

/**
 * Unified hook for fetching a single asset's USD price.
 *
 * Resolution order:
 *  1. Indexer API           (cached, free)
 *  2. Shared on-chain cache (populated by useBatchAssetPrices on listing pages)
 *  3. VaultLens             (lending vaults — reads the vault's own oracle)
 *  4. VaultLens batch on lending vault strategies (Earn vaults)
 *  5. EulerEarnVaultLens → strategy eVault → VaultLens (last-resort fallback)
 */
export function useAssetPrice({
  assetAddress,
  vaultAddress,
  lendingVaultAddresses,
  chainId = E_LEND_CHAIN_ID,
  enabled = true,
}: UseAssetPriceParams): UseAssetPriceResult {
  const queryClient = useQueryClient()

  // ── Tier 1: Indexer ──────────────────────────────────────────────────
  const { data: indexerMap, isLoading: isIndexerLoading } = usePricesFromIndexer(chainId)
  const indexerPrice = useMemo(() => getPriceFromMap(indexerMap, assetAddress), [indexerMap, assetAddress])
  const hasIndexerPrice = indexerPrice > 0

  // ── Tier 2: Shared on-chain cache (by asset address) ─────────────────
  const onchainCache = usePriceCache(ONCHAIN_PRICE_CACHE_KEY)
  const cachedPrice = useMemo(() => getCachedPrice(onchainCache, assetAddress), [onchainCache, assetAddress])
  const hasCachedPrice = cachedPrice > 0

  // ── Tier 2b: VaultLens cache (by vault address) ──────────────────────
  const vaultLensCache = usePriceCache(VAULTLENS_PRICE_CACHE_KEY)
  const cachedVaultLensPrice = useMemo(
    () => getCachedPrice(vaultLensCache, vaultAddress),
    [vaultLensCache, vaultAddress],
  )
  const hasCachedVaultLensPrice = cachedVaultLensPrice > 0

  const needsOnchain = enabled && !hasIndexerPrice && !hasCachedPrice && !hasCachedVaultLensPrice

  // ── Tier 3: VaultLens (lending vaults) ───────────────────────────────
  const shouldFetchVaultLens = needsOnchain && !!vaultAddress

  const { data: vaultLensData, isLoading: isVaultLensLoading } = useReadContract({
    address: VAULT_LENS_ADDRESS as Address,
    abi: vaultLensAbi,
    functionName: 'getVaultInfoFull',
    args: vaultAddress ? [vaultAddress] : undefined,
    chainId,
    query: { enabled: shouldFetchVaultLens, staleTime: 60_000 },
  })

  const vaultLensPrice = useMemo(() => extractVaultLensPrice(vaultLensData), [vaultLensData])
  const hasVaultLensPrice = vaultLensPrice > 0

  // ── Tier 4: VaultLens batch on lending vaults / strategies ────────────
  const hasLendingAddresses = !!lendingVaultAddresses && lendingVaultAddresses.length > 0
  const shouldFetchLending = needsOnchain && !hasVaultLensPrice && !isVaultLensLoading && hasLendingAddresses

  const lendingContracts = useMemo(
    () =>
      (lendingVaultAddresses ?? []).map((addr) => ({
        address: VAULT_LENS_ADDRESS as Address,
        abi: vaultLensAbi,
        functionName: 'getVaultInfoFull' as const,
        args: [addr] as const,
        chainId,
      })),
    [lendingVaultAddresses, chainId],
  )

  const { data: lendingBatchData, isLoading: isLendingLoading } = useReadContracts({
    contracts: lendingContracts,
    query: { enabled: shouldFetchLending, staleTime: 60_000 },
  })

  const lendingPrice = useMemo(() => {
    if (!lendingBatchData) return 0
    for (const r of lendingBatchData) {
      if (r?.status !== 'success') continue
      const price = extractVaultLensPrice(r.result)
      if (price > 0) return price
    }
    return 0
  }, [lendingBatchData])
  const hasLendingPrice = lendingPrice > 0

  // ── Tier 5: EulerEarnVaultLens → strategy → VaultLens (last resort) ──
  const shouldFetchEarn =
    shouldFetchVaultLens && !hasVaultLensPrice && !hasLendingPrice && !isVaultLensLoading && !isLendingLoading

  const { data: earnData, isLoading: isEarnLoading } = useReadContract({
    address: EULER_EARN_VAULT_LENS_ADDRESS as Address,
    abi: eulerEarnVaultLensAbi,
    functionName: 'getVaultInfoFull',
    args: vaultAddress ? [vaultAddress] : undefined,
    chainId,
    query: { enabled: shouldFetchEarn, staleTime: 60_000 },
  })

  const strategyAddress = useMemo(() => extractEVaultStrategyAddress(earnData), [earnData])

  const { data: strategyLensData, isLoading: isStrategyLoading } = useReadContract({
    address: VAULT_LENS_ADDRESS as Address,
    abi: vaultLensAbi,
    functionName: 'getVaultInfoFull',
    args: strategyAddress ? [strategyAddress] : undefined,
    chainId,
    query: { enabled: !!strategyAddress, staleTime: 60_000 },
  })

  const earnPrice = useMemo(() => extractVaultLensPrice(strategyLensData), [strategyLensData])
  const hasEarnPrice = earnPrice > 0

  // ── Write resolved on-chain prices to both caches ─────────────────────
  const resolvedPrice = hasVaultLensPrice
    ? vaultLensPrice
    : hasLendingPrice
      ? lendingPrice
      : hasEarnPrice
        ? earnPrice
        : 0

  useEffect(() => {
    if (resolvedPrice <= 0) return
    if (assetAddress) {
      const cacheKey = assetAddress.toLowerCase()
      const current = queryClient.getQueryData<PriceCache>(ONCHAIN_PRICE_CACHE_KEY)
      if (current?.get(cacheKey) !== resolvedPrice) {
        queryClient.setQueryData<PriceCache>(ONCHAIN_PRICE_CACHE_KEY, (old) => {
          const map = new Map(old)
          map.set(cacheKey, resolvedPrice)
          return map
        })
      }
    }
    if (vaultAddress) {
      const cacheKey = vaultAddress.toLowerCase()
      const current = queryClient.getQueryData<PriceCache>(VAULTLENS_PRICE_CACHE_KEY)
      if (current?.get(cacheKey) !== resolvedPrice) {
        queryClient.setQueryData<PriceCache>(VAULTLENS_PRICE_CACHE_KEY, (old) => {
          const map = new Map(old)
          map.set(cacheKey, resolvedPrice)
          return map
        })
      }
    }
  }, [resolvedPrice, assetAddress, vaultAddress, queryClient])

  // ── Result ───────────────────────────────────────────────────────────
  if (hasIndexerPrice) return { priceUSD: indexerPrice, isLoading: false, source: 'indexer' }
  if (hasCachedPrice) return { priceUSD: cachedPrice, isLoading: false, source: 'vaultLens' }
  if (hasCachedVaultLensPrice) return { priceUSD: cachedVaultLensPrice, isLoading: false, source: 'vaultLens' }
  if (hasVaultLensPrice) return { priceUSD: vaultLensPrice, isLoading: false, source: 'vaultLens' }
  if (hasLendingPrice) return { priceUSD: lendingPrice, isLoading: false, source: 'lendingVault' }
  if (hasEarnPrice) return { priceUSD: earnPrice, isLoading: false, source: 'earnStrategy' }

  const isLoading =
    (enabled && isIndexerLoading) ||
    (shouldFetchVaultLens && isVaultLensLoading) ||
    (shouldFetchLending && isLendingLoading) ||
    (shouldFetchEarn && (isEarnLoading || isStrategyLoading))

  return { priceUSD: 0, isLoading, source: 'none' }
}

function hasAllPrices(cache: PriceCache | undefined, prices: PriceCache): boolean {
  if (!cache) return false
  for (const [addr, price] of prices) {
    if (price > 0 && cache.get(addr) !== price) return false
  }
  return true
}

// ---------------------------------------------------------------------------
// Batch asset prices
// ---------------------------------------------------------------------------

interface UseBatchAssetPricesParams {
  vaultAddresses: (Address | undefined)[]
  /** Underlying asset addresses (parallel to vaultAddresses).
   *  Required for Earn vaults where VaultLens reverts — lets us
   *  fall back to the indexer using the known asset address. */
  assetAddresses?: (Address | undefined)[]
  /** Lending vault (strategy) addresses. VaultLens on these gives us
   *  on-chain asset prices we can reuse for Earn vaults. */
  lendingVaultAddresses?: Address[]
  chainId?: number
  enabled?: boolean
}

interface UseBatchAssetPricesResult {
  /** vault address (lowercase) → USD price */
  priceMap: Map<string, number>
  /** underlying asset address (lowercase) → USD price */
  assetPriceMap: Map<string, number>
  isPending: boolean
}

/**
 * Batch fetch USD prices for vault underlying assets.
 *
 * Resolution per vault:
 *  1. Indexer (cached, free)
 *  2. VaultLens on-chain (lending vaults — Earn vaults will fail gracefully)
 *
 * Pass `assetAddresses` so the indexer can still be checked when VaultLens
 * fails for Earn vaults.
 */
export function useBatchAssetPrices({
  vaultAddresses,
  assetAddresses,
  lendingVaultAddresses,
  chainId = E_LEND_CHAIN_ID,
  enabled = true,
}: UseBatchAssetPricesParams): UseBatchAssetPricesResult {
  const queryClient = useQueryClient()

  const { validAddresses, validAssetAddresses } = useMemo(() => {
    const addrs: Address[] = []
    const assets: (Address | undefined)[] = []
    const seen = new Set<string>()
    for (let i = 0; i < vaultAddresses.length; i++) {
      const addr = vaultAddresses[i]
      if (!addr) continue
      const lower = addr.toLowerCase()
      if (seen.has(lower)) continue
      seen.add(lower)
      addrs.push(addr)
      assets.push(assetAddresses?.[i])
    }
    return { validAddresses: addrs, validAssetAddresses: assets }
  }, [vaultAddresses, assetAddresses])

  // Indexer prices (shared cache)
  const { data: indexerMap, isPending: isIndexerPending } = usePricesFromIndexer(chainId)

  // VaultLens batch (succeeds for lending vaults, fails for Earn vaults)
  const contracts = useMemo(
    () =>
      validAddresses.map((address) => ({
        address: VAULT_LENS_ADDRESS as Address,
        abi: vaultLensAbi,
        functionName: 'getVaultInfoFull' as const,
        args: [address] as const,
        chainId,
      })),
    [validAddresses, chainId],
  )

  const { data, isPending } = useReadContracts({
    contracts,
    query: {
      enabled: enabled && validAddresses.length > 0,
      staleTime: 60_000,
      refetchInterval: 60_000,
    },
  })

  // VaultLens batch on lending vault (strategy) addresses — gives us prices
  // that Earn vaults can reuse since they share the same underlying asset.
  const uniqueLendingAddresses = useMemo(() => {
    if (!lendingVaultAddresses || lendingVaultAddresses.length === 0) return []
    const seen = new Set<string>()
    return lendingVaultAddresses.filter((addr) => {
      const lower = addr.toLowerCase()
      if (seen.has(lower)) return false
      seen.add(lower)
      return true
    })
  }, [lendingVaultAddresses])

  const lendingContracts = useMemo(
    () =>
      uniqueLendingAddresses.map((address) => ({
        address: VAULT_LENS_ADDRESS as Address,
        abi: vaultLensAbi,
        functionName: 'getVaultInfoFull' as const,
        args: [address] as const,
        chainId,
      })),
    [uniqueLendingAddresses, chainId],
  )

  const { data: lendingData, isPending: isLendingPending } = useReadContracts({
    contracts: lendingContracts,
    query: {
      enabled: enabled && uniqueLendingAddresses.length > 0,
      staleTime: 60_000,
      refetchInterval: 60_000,
    },
  })

  // Build lending price map: asset address (lowercase) → price
  // Sources: main VaultLens batch (lending vaults) + lending vault batch (strategies).
  const lendingPricesByAsset = useMemo(() => {
    const map = new Map<string, number>()
    const allResults = [...(data ?? []), ...(lendingData ?? [])]
    for (const result of allResults) {
      if (result?.status !== 'success' || !result.result) continue
      const info = result.result as any
      const asset = info.asset as string | undefined
      if (!asset) continue
      const price = extractVaultLensPrice(info)
      if (price > 0) {
        map.set(asset.toLowerCase(), price)
      }
    }
    return map
  }, [data, lendingData])

  // Assemble final price maps
  const { priceMap, assetPriceMap } = useMemo(() => {
    const vaultMap = new Map<string, number>()
    const assetMap = new Map<string, number>()

    for (let i = 0; i < validAddresses.length; i++) {
      const vaultAddr = validAddresses[i]
      if (!vaultAddr) continue

      const lensResult = data?.[i]
      const vaultInfo = lensResult?.status === 'success' ? (lensResult.result as any) : undefined
      const assetAddress: Address | undefined = (vaultInfo?.asset as Address | undefined) || validAssetAddresses[i]

      // Tier 1: Indexer
      let priceUSD = getPriceFromMap(indexerMap, assetAddress)

      // Tier 2: VaultLens on the vault itself (lending vaults)
      if (priceUSD === 0 && vaultInfo) {
        priceUSD = extractVaultLensPrice(vaultInfo)
      }

      // Tier 3: Reuse lending vault price by asset address (Earn vaults)
      if (priceUSD === 0 && assetAddress) {
        priceUSD = lendingPricesByAsset.get(assetAddress.toLowerCase()) ?? 0
      }

      if (priceUSD > 0) {
        vaultMap.set(vaultAddr.toLowerCase(), priceUSD)
        if (assetAddress) {
          assetMap.set((assetAddress as string).toLowerCase(), priceUSD)
        }
      }
    }

    return { priceMap: vaultMap, assetPriceMap: assetMap }
  }, [data, validAddresses, validAssetAddresses, indexerMap, lendingPricesByAsset])

  // Write resolved on-chain prices to shared cache (by asset address)
  useEffect(() => {
    if (assetPriceMap.size === 0) return
    const current = queryClient.getQueryData<PriceCache>(ONCHAIN_PRICE_CACHE_KEY)
    if (hasAllPrices(current, assetPriceMap)) return

    queryClient.setQueryData<PriceCache>(ONCHAIN_PRICE_CACHE_KEY, (old) => {
      const map = new Map(old)
      for (const [addr, price] of assetPriceMap) {
        if (price > 0) map.set(addr, price)
      }
      return map
    })
  }, [assetPriceMap, queryClient])

  // Write resolved VaultLens prices to VaultLens cache (by vault address)
  useEffect(() => {
    if (priceMap.size === 0) return
    const current = queryClient.getQueryData<PriceCache>(VAULTLENS_PRICE_CACHE_KEY)
    if (hasAllPrices(current, priceMap)) return

    queryClient.setQueryData<PriceCache>(VAULTLENS_PRICE_CACHE_KEY, (old) => {
      const map = new Map(old)
      for (const [vaultAddr, price] of priceMap) {
        if (price > 0) map.set(vaultAddr, price)
      }
      return map
    })
  }, [priceMap, queryClient])

  const isVaultLensPending = enabled && validAddresses.length > 0 && isPending
  const isLendingBatchPending = enabled && uniqueLendingAddresses.length > 0 && isLendingPending

  return { priceMap, assetPriceMap, isPending: isVaultLensPending || isIndexerPending || isLendingBatchPending }
}
